import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { cookieCart, CartItem, LocalCart, cookieProjectDetails } from '@/lib/cookieCart';
import { cookieUtils } from '@/lib/cookies';
import { supabase } from '@/integrations/supabase/client';

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  orderId: string | null;
  loading: boolean;
  addItem: (item: CartItem) => Promise<void>;
  removeItem: (job_position_id: string, seniority_level?: number, english_level?: string) => Promise<void>;
  updateItemQuantity: (job_position_id: string, quantity: number, seniority_level?: number, english_level?: string) => Promise<void>;
  updateQuantity: (job_position_id: string, seniority_level: number, english_level: string, quantity: number) => Promise<void>;
  clearCart: () => void;
  syncToOrder: () => Promise<string>; // Returns order_id
  refreshCart: () => Promise<void>;
  copyItemsFromSharedOrder: (sharedOrderId: string) => Promise<string>; // Returns new order_id
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [itemCount, setItemCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Load cart on mount and when user changes
  useEffect(() => {
    const initCart = async () => {
      console.log('[CartContext] initCart starting, user:', user?.id || 'null/anonymous');
      console.log('[CartContext] User object:', { hasUser: !!user, userId: user?.id });
      setLoading(true);
      
      if (user && user.id) {
        // Check if there's a local cart to sync
        const localCart = cookieCart.get();
        console.log('[CartContext] Local cart check:', { 
          exists: !!localCart, 
          itemCount: localCart?.items.length || 0 
        });
        
        if (localCart && localCart.items.length > 0) {
          console.log('[CartContext] User logged in with local cart, syncing...');
          try {
            const newOrderId = await syncToOrderInternal(localCart);
            console.log('[CartContext] Local cart synced successfully, orderId:', newOrderId);
          } catch (error) {
            console.error('[CartContext] Error syncing local cart:', error);
            // Fall back to loading user order
            await loadUserOrder();
          }
        } else {
          await loadUserOrder();
        }
      } else {
        console.log('[CartContext] No user, loading local cart');
        loadLocalCart();
        console.log('[CartContext] Local cart loaded:', {
          itemCount: cookieCart.getItemCount(),
          items: cookieCart.get()?.items.length || 0
        });
      }
      
      // Get final state after loading
      const finalCart = user ? null : cookieCart.get();
      const finalState = {
        items: finalCart?.items.length || 0,
        itemCount: cookieCart.getItemCount(),
        orderId: orderId
      };
      
      setLoading(false);
      console.log('[CartContext] initCart completed, final state:', finalState);
    };

    initCart();
  }, [user]);
  
  // Listen for order status changes (when step becomes ORDER_ACCEPTED)
  useEffect(() => {
    if (!user || !orderId) return;
    
    const checkOrderStatus = async () => {
      try {
        const { data: order, error } = await supabase
          .from('orders')
          .select('step')
          .eq('id', orderId)
          .single();
        
        if (error || !order) return;
        
        // If order is no longer in CART_STARTED state, clear cart
        if (order.step !== 'CART_STARTED') {
          console.log('[CartContext] Order step changed to:', order.step, 'clearing cart');
          setItems([]);
          setItemCount(0);
          setOrderId(null);
        }
      } catch (error) {
        console.error('[CartContext] Error checking order status:', error);
      }
    };
    
    // Check status immediately
    checkOrderStatus();
    
    // Check every 2 seconds for order status changes
    const interval = setInterval(checkOrderStatus, 2000);
    
    return () => clearInterval(interval);
  }, [user, orderId]);

  // Internal sync function to avoid closure issues
  const syncToOrderInternal = async (localCart: LocalCart): Promise<string> => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    console.log('[CartContext] syncToOrderInternal starting', {
      userId: user.id,
      itemsCount: localCart.items?.length || 0,
      items: localCart.items
    });
    
    // Validate cart has items
    if (!localCart.items || localCart.items.length === 0) {
      console.error('[CartContext] No items in cart to sync');
      throw new Error('No items in cart to sync');
    }
    
    // Get user information from metadata
    const userFullName = user.user_metadata?.full_name || user.user_metadata?.name || null;
    const userPhone = user.user_metadata?.phone || null;
    const userEmail = user.email || null;
    
    const orderData = {
      user_id: user.id,
      company_name: localCart.company_name || null,
      contact_name: localCart.contact_name || userFullName || null,
      email: localCart.email || userEmail || null,
      phone: localCart.phone || userPhone || null,
      company_type: localCart.company_type || 'others',
      status: 'STARTED',
      step: 'CART_STARTED'
    };
    
    console.log('[CartContext] Attempting to create order with data:', orderData);
    
    // Create order with metadata (allow nulls for metadata fields)
    // Priority: user metadata (if exists) > cookie cart data > user email
    const { data: newOrder, error: orderError } = await supabase
      .from('orders')
      .insert(orderData)
      .select()
      .single();
    
    if (orderError) {
      console.error('[CartContext] Error creating order during sync:', {
        error: orderError,
        code: orderError.code,
        message: orderError.message,
        details: orderError.details,
        hint: orderError.hint
      });
      const errorMessage = orderError.message || 'Failed to create order';
      throw new Error(`Failed to create order: ${errorMessage} (Code: ${orderError.code})`);
    }
    
    if (!newOrder) {
      console.error('[CartContext] Order creation returned null/undefined');
      throw new Error('Failed to create order: No order data returned');
    }
    
    console.log('[CartContext] Order created successfully:', newOrder.id);
    
    // Fetch job positions to calculate amount if needed
    const jobPositionIds = [...new Set(localCart.items.map(item => item.job_position_id))];
    const { data: jobPositions, error: positionsError } = await supabase
      .from('job_positions')
      .select('id, rate_hourly_junior, rate_hourly_mid, rate_hourly_senior, rate_hourly_specialist')
      .in('id', jobPositionIds);
    
    if (positionsError) {
      console.warn('[CartContext] Error fetching job positions for amount calculation:', positionsError);
    }
    
    const positionsMap = new Map(
      (jobPositions || []).map(pos => [pos.id, pos])
    );
    
    // Default contract duration if not specified (6 months)
    const defaultContractDuration = 6;
    const hoursPerMonth = 160; // 40h/week * 4 weeks
    
    // Insert order items with calculated amount and contract_duration if missing
    const orderItems = localCart.items
      .filter(item => {
        // Validate required fields
        if (!item.job_position_id) {
          console.warn('[CartContext] Skipping item without job_position_id:', item);
          return false;
        }
        if (item.quantity === undefined || item.quantity <= 0) {
          console.warn('[CartContext] Skipping item with invalid quantity:', item);
          return false;
        }
        if (item.seniority_level === undefined || item.seniority_level === null) {
          console.warn('[CartContext] Skipping item without seniority_level:', item);
          return false;
        }
        return true;
      })
      .map(item => {
        // Get hourly rate from job position
        const position = positionsMap.get(item.job_position_id);
        let hourlyRate = 50; // fallback
        
        if (position) {
          switch (item.seniority_level) {
            case 0: // Junior
              hourlyRate = Number(position.rate_hourly_junior) || 35;
              break;
            case 1: // Mid
              hourlyRate = Number(position.rate_hourly_mid) || 50;
              break;
            case 2: // Senior
              hourlyRate = Number(position.rate_hourly_senior) || 70;
              break;
            case 3: // Specialist
              hourlyRate = Number(position.rate_hourly_specialist) || 90;
              break;
          }
        }
        
        // Calculate amount if not present: hourly_rate * quantity * hours_per_month * contract_duration
        const contractDuration = item.contract_duration ?? defaultContractDuration;
        const amount = item.amount ?? (hourlyRate * item.quantity * hoursPerMonth * contractDuration);
        
        // Ensure english_level has a default value
        const englishLevel = item.english_level || 'fluent';
        
        return {
          order_id: newOrder.id,
          job_position_id: item.job_position_id,
          quantity: item.quantity,
          seniority_level: item.seniority_level,
          english_level: englishLevel,
          contract_duration: contractDuration,
          amount: amount
        };
      });
    
    // Check if we have valid items to insert
    if (orderItems.length === 0) {
      console.error('[CartContext] No valid items to insert after validation');
      throw new Error('No valid items in cart to sync');
    }
    
    console.log('[CartContext] Attempting to insert order items:', {
      count: orderItems.length,
      items: orderItems.map(item => ({
        job_position_id: item.job_position_id,
        quantity: item.quantity,
        seniority_level: item.seniority_level,
        english_level: item.english_level
      }))
    });
    
    const { data: insertedItems, error: itemsError } = await supabase
      .from('order_items')
      .insert(orderItems)
      .select();
    
    if (itemsError) {
      console.error('[CartContext] Error inserting items during sync:', {
        error: itemsError,
        code: itemsError.code,
        message: itemsError.message,
        details: itemsError.details,
        hint: itemsError.hint
      });
      console.error('[CartContext] Attempted to insert items:', orderItems);
      const errorMessage = itemsError.message || 'Failed to add items to order';
      throw new Error(`Failed to add items to order: ${errorMessage} (Code: ${itemsError.code})`);
    }
    
    if (!insertedItems || insertedItems.length === 0) {
      console.error('[CartContext] No items were inserted');
      throw new Error('Failed to add items to order: No items were inserted');
    }
    
    console.log('[CartContext] Order items created:', insertedItems.length);
    
    // Get job positions and their skills for each inserted item
    // Reuse jobPositionIds already declared above
    const { data: jobPositionsWithSkills, error: skillsError } = await supabase
      .from('job_position_skills')
      .select(`
        job_position_id,
        skill_id
      `)
      .in('job_position_id', jobPositionIds);
    
    if (skillsError) {
      console.error('[CartContext] Error fetching job position skills:', skillsError);
      console.log('[CartContext] Continuing without skill relations...');
    } else if (jobPositionsWithSkills && jobPositionsWithSkills.length > 0) {
      // Create order_items_skill relationships
      const orderItemsSkills = insertedItems.flatMap(orderItem => {
        const matchingSkills = jobPositionsWithSkills.filter(
          jps => jps.job_position_id === orderItem.job_position_id
        );
        
        return matchingSkills.map(jps => ({
          order_item_id: orderItem.id,
          skill_id: jps.skill_id
        }));
      });
      
      if (orderItemsSkills.length > 0) {
        try {
          const { error: skillRelationError } = await supabase
            .from('order_items_skill')
            .insert(orderItemsSkills);
          
          if (skillRelationError) {
            console.error('[CartContext] Error creating skill relations:', skillRelationError);
            console.log('[CartContext] Continuing without skill relations...');
          } else {
            console.log('[CartContext] Created', orderItemsSkills.length, 'skill relations');
          }
        } catch (err) {
          console.error('[CartContext] Exception creating skill relations:', err);
          console.log('[CartContext] Continuing without skill relations...');
        }
      }
    }
    
    console.log('[CartContext] Items synced, syncing project details');
    
    // Sync project details from cookies if they exist
    const localProjectDetails = cookieProjectDetails.get();
    if (localProjectDetails) {
      console.log('[CartContext] Found project details in cookies, syncing to database');
      try {
        // Only sync if there's actual content (not just empty strings)
        const hasContent = Object.values(localProjectDetails).some(
          val => typeof val === 'string' && val.trim().length > 0 && !val.startsWith('pending_upload_')
        );
        
        if (hasContent) {
          const { error: projectDetailsError } = await supabase
            .from('project_details')
            .upsert({
              order_id: newOrder.id,
              project_description: localProjectDetails.projectDescription || null,
              team_info: localProjectDetails.teamInfo || null,
              required_software: localProjectDetails.requiredSoftware || null,
              job_description_url: localProjectDetails.jobDescriptionUrl || null,
              responsibilities: localProjectDetails.responsibilities || null,
              job_description_file_path: localProjectDetails.jobDescriptionFilePath?.startsWith('pending_upload_') 
                ? null 
                : localProjectDetails.jobDescriptionFilePath || null
            }, {
              onConflict: 'order_id',
              ignoreDuplicates: false
            });
          
          if (projectDetailsError) {
            console.error('[CartContext] Error syncing project details:', projectDetailsError);
            console.log('[CartContext] Continuing without project details...');
          } else {
            console.log('[CartContext] Project details synced successfully');
          }
        }
      } catch (err) {
        console.error('[CartContext] Exception syncing project details:', err);
        console.log('[CartContext] Continuing without project details...');
      }
    }
    
    console.log('[CartContext] Clearing cookie cart and project details');
    
    // Clear cookie cart and project details
    cookieCart.clear();
    cookieProjectDetails.clear();
    
    // Update state immediately
    setOrderId(newOrder.id);
    const orderItemsData = insertedItems.map(item => ({
      job_position_id: item.job_position_id,
      quantity: item.quantity,
      seniority_level: item.seniority_level,
      english_level: item.english_level
    }));
    setItems(orderItemsData);
    setItemCount(orderItemsData.reduce((sum: number, item: CartItem) => sum + item.quantity, 0));
    
    console.log('[CartContext] State updated with new order');
    
    return newOrder.id;
  };

  const loadLocalCart = () => {
    console.log('[CartContext] Loading local cart from cookies');
    const cart = cookieCart.get();
    const cartItems = cart?.items || [];
    const totalCount = cookieCart.getItemCount();
    
    console.log('[CartContext] Local cart loaded:', {
      itemCount: cartItems.length,
      totalQuantity: totalCount,
      items: cartItems
    });
    
    setItems(cartItems);
    setItemCount(totalCount);
    setOrderId(null);
  };

  const loadUserOrder = async () => {
    if (!user) return;
    
    console.log('[CartContext] Loading user order from database');
    try {
      const { data: orders, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_items (
            job_position_id,
            quantity,
            seniority_level,
            english_level
          )
        `)
        .eq('user_id', user.id)
        .eq('step', 'CART_STARTED')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (error) {
        console.error('[CartContext] Error loading user order:', error);
        return;
      }

      if (orders && orders.length > 0) {
        const order = orders[0];
        setOrderId(order.id);
        const orderItems = (order.order_items || []) as CartItem[];
        setItems(orderItems);
        setItemCount(orderItems.reduce((sum, item) => sum + item.quantity, 0));
        console.log('[CartContext] User order loaded:', {
          orderId: order.id,
          itemCount: orderItems.length
        });
      } else {
        console.log('[CartContext] No active order found for user, clearing cart');
        setItems([]);
        setItemCount(0);
        setOrderId(null);
      }
    } catch (error) {
      console.error('[CartContext] Exception loading user order:', error);
    }
  };

  const addItem = async (item: CartItem) => {
    console.log('[CartContext] Adding item:', item);
    console.log('[CartContext] User state:', { 
      hasUser: !!user, 
      userId: user?.id, 
      isAnonymous: !user 
    });
    
    if (user) {
      // Authenticated: add to database
      let currentOrderId = orderId;
      
      // If no orderId in state, try to load existing active order
      if (!currentOrderId) {
        console.log('[CartContext] No orderId in state, checking for existing order');
        try {
          const { data: existingOrders, error: checkError } = await supabase
            .from('orders')
            .select('id')
            .eq('user_id', user.id)
            .eq('step', 'CART_STARTED')
            .order('created_at', { ascending: false })
            .limit(1);
          
          if (checkError) {
            console.error('[CartContext] Error checking for existing order:', checkError);
          } else if (existingOrders && existingOrders.length > 0) {
            currentOrderId = existingOrders[0].id;
            setOrderId(currentOrderId);
            console.log('[CartContext] Found existing order:', currentOrderId);
          }
        } catch (error) {
          console.error('[CartContext] Exception checking for existing order:', error);
        }
      }
      
      // If still no order, create a new one
      if (!currentOrderId) {
        console.log('[CartContext] Creating new order for authenticated user');
        const { data: newOrder, error: orderError } = await supabase
          .from('orders')
          .insert({
            user_id: user.id,
            status: 'STARTED',
            step: 'CART_STARTED'
          })
          .select()
          .single();
        
        if (orderError) {
          console.error('[CartContext] Error creating order:', orderError);
          throw new Error('Failed to create order');
        }
        
        if (newOrder) {
          currentOrderId = newOrder.id;
          setOrderId(currentOrderId);
          console.log('[CartContext] Order created:', currentOrderId);
        }
      }
      
      if (currentOrderId) {
        // Fetch job position to calculate amount if needed
        const { data: position, error: positionError } = await supabase
          .from('job_positions')
          .select('rate_hourly_junior, rate_hourly_mid, rate_hourly_senior, rate_hourly_specialist')
          .eq('id', item.job_position_id)
          .single();
        
        let hourlyRate = 50; // fallback
        if (!positionError && position) {
          switch (item.seniority_level) {
            case 0: // Junior
              hourlyRate = Number(position.rate_hourly_junior) || 35;
              break;
            case 1: // Mid
              hourlyRate = Number(position.rate_hourly_mid) || 50;
              break;
            case 2: // Senior
              hourlyRate = Number(position.rate_hourly_senior) || 70;
              break;
            case 3: // Specialist
              hourlyRate = Number(position.rate_hourly_specialist) || 90;
              break;
          }
        }
        
        // Default contract duration if not specified (6 months)
        const defaultContractDuration = 6;
        const hoursPerMonth = 160; // 40h/week * 4 weeks
        const contractDuration = item.contract_duration ?? defaultContractDuration;
        const amount = item.amount ?? (hourlyRate * item.quantity * hoursPerMonth * contractDuration);
        
        const { error: itemError } = await supabase
          .from('order_items')
          .insert({
            order_id: currentOrderId,
            job_position_id: item.job_position_id,
            quantity: item.quantity,
            seniority_level: item.seniority_level,
            english_level: item.english_level,
            contract_duration: contractDuration,
            amount: amount
          });
        
        if (itemError) {
          console.error('[CartContext] Error adding item:', itemError);
          throw new Error('Failed to add item to order');
        }
        
        console.log('[CartContext] Item added to order, reloading...');
        await loadUserOrder();
      }
    } else {
      // Anonymous: add to cookie
      console.log('[CartContext] Adding item to cookie cart (anonymous user)');
      try {
        cookieCart.addItem(item);
        
        // Read immediately after writing to ensure state sync
        const updatedCart = cookieCart.get();
        const updatedItems = updatedCart?.items || [];
        const updatedCount = cookieCart.getItemCount();
        
        console.log('[CartContext] Item added successfully to cookie cart');
        console.log('[CartContext] Cart from cookie:', {
          itemsCount: updatedItems.length,
          totalQuantity: updatedCount,
          items: updatedItems
        });
        
        // Update state directly with fresh data from cookie
        // Use functional updates to ensure React detects the change
        setItems(() => updatedItems);
        setItemCount(() => updatedCount);
        setOrderId(null);
        
        // Verify state was updated by reading from cookie again
        const verificationCart = cookieCart.get();
        const verificationCount = cookieCart.getItemCount();
        
        console.log('[CartContext] State updated:', {
          items: updatedItems.length,
          itemCount: updatedCount,
          verification: {
            cookieItems: verificationCart?.items.length || 0,
            cookieCount: verificationCount
          }
        });
        
        // Force a re-render by updating state again if there's a mismatch
        if (verificationCount !== updatedCount || (verificationCart?.items.length || 0) !== updatedItems.length) {
          console.warn('[CartContext] State mismatch detected, correcting...');
          setItems(verificationCart?.items || []);
          setItemCount(verificationCount);
        }
      } catch (error) {
        console.error('[CartContext] Error adding item to cookie cart:', error);
        throw error;
      }
    }
  };

  const removeItem = async (job_position_id: string, seniority_level?: number, english_level?: string) => {
    console.log('[CartContext] Removing item:', { job_position_id, seniority_level, english_level });
    
    if (user && orderId) {
      // If seniority/english not provided, remove all variations of this job position
      let query = supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderId)
        .eq('job_position_id', job_position_id);
      
      if (seniority_level !== undefined) {
        query = query.eq('seniority_level', seniority_level);
      }
      if (english_level !== undefined) {
        query = query.eq('english_level', english_level);
      }
      
      const { error } = await query;
      
      if (error) {
        console.error('[CartContext] Error removing item:', error);
        throw new Error('Failed to remove item');
      }
      
      await loadUserOrder();
    } else {
      if (seniority_level !== undefined && english_level !== undefined) {
        cookieCart.removeItem(job_position_id, seniority_level, english_level);
      } else {
        // Remove all variations from cookie cart
        const cart = cookieCart.get();
        if (cart) {
          cart.items = cart.items.filter(item => item.job_position_id !== job_position_id);
          cookieCart.set(cart);
        }
      }
      loadLocalCart();
    }
  };

  const updateQuantity = async (job_position_id: string, seniority_level: number, english_level: string, quantity: number) => {
    console.log('[CartContext] Updating quantity:', { job_position_id, quantity });
    
    if (user && orderId) {
      if (quantity <= 0) {
        await removeItem(job_position_id, seniority_level, english_level);
      } else {
        const { error } = await supabase
          .from('order_items')
          .update({ quantity })
          .eq('order_id', orderId)
          .eq('job_position_id', job_position_id)
          .eq('seniority_level', seniority_level)
          .eq('english_level', english_level);
        
        if (error) {
          console.error('[CartContext] Error updating quantity:', error);
          throw new Error('Failed to update quantity');
        }
        
        await loadUserOrder();
      }
    } else {
      cookieCart.updateQuantity(job_position_id, seniority_level, english_level, quantity);
      loadLocalCart();
    }
  };

  const clearCart = () => {
    console.log('[CartContext] Clearing cart');
    // Clear cookie cart regardless of user status
    cookieCart.clear();
    cookieProjectDetails.clear();
    // Clear localStorage orderId
    cookieUtils.removeOrderId();
    // Clear state
    setItems([]);
    setItemCount(0);
    setOrderId(null);
  };

  const syncToOrder = async (): Promise<string> => {
    console.log('[CartContext] syncToOrder (public) called', {
      user: user ? { id: user.id, email: user.email } : null,
      stateItemsCount: items.length,
      stateOrderId: orderId
    });
    
    // Try to get cart from cookie
    const localCart = cookieCart.get();
    console.log('[CartContext] Cookie cart:', localCart);
    
    // If no cookie cart, try to use state items
    if (!localCart || !localCart.items || localCart.items.length === 0) {
      console.warn('[CartContext] No cookie cart found, trying to use state items');
      
      // Use state items if available
      if (items.length > 0) {
        console.log('[CartContext] Using state items to create cart');
        const stateCart: LocalCart = {
          items: items.map(item => ({
            job_position_id: item.job_position_id,
            quantity: item.quantity,
            seniority_level: item.seniority_level,
            english_level: item.english_level,
            contract_duration: item.contract_duration,
            amount: undefined
          })),
          company_name: undefined,
          contact_name: undefined,
          email: undefined,
          phone: undefined,
          company_type: undefined
        };
        return await syncToOrderInternal(stateCart);
      }
      
      throw new Error('No items in cart to sync');
    }
    
    return await syncToOrderInternal(localCart);
  };

  const refreshCart = async () => {
    if (user) {
      await loadUserOrder();
      
      // After loading, if no active order found, clear cart
      // This happens when order step changes to ORDER_ACCEPTED
      const { data: orders } = await supabase
        .from('orders')
        .select('id')
        .eq('user_id', user.id)
        .eq('step', 'CART_STARTED')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (!orders || orders.length === 0) {
        console.log('[CartContext] No active order, clearing cart state');
        setItems([]);
        setItemCount(0);
        setOrderId(null);
      }
    } else {
      loadLocalCart();
    }
  };

  // Simplified wrapper for updateQuantity (most common use case)
  const updateItemQuantity = async (job_position_id: string, quantity: number, seniority_level?: number, english_level?: string) => {
    // Get defaults from first matching item if not provided
    const currentItem = items.find(item => item.job_position_id === job_position_id);
    const effectiveSeniority = seniority_level ?? currentItem?.seniority_level ?? 2;
    const effectiveEnglish = english_level ?? currentItem?.english_level ?? 'fluent';
    
    await updateQuantity(job_position_id, effectiveSeniority, effectiveEnglish, quantity);
  };

  // Copy items from a shared order to user's cart (creates new order)
  const copyItemsFromSharedOrder = async (sharedOrderId: string): Promise<string> => {
    if (!user) {
      throw new Error('User must be authenticated to copy shared order');
    }
    
    console.log('[CartContext] Copying items from shared order:', sharedOrderId);
    
    // Fetch order items from shared order
    const { data: sharedItems, error: fetchError } = await supabase
      .from('order_items')
      .select('*')
      .eq('order_id', sharedOrderId);
    
    if (fetchError) {
      console.error('[CartContext] Error fetching shared order items:', fetchError);
      throw new Error('Failed to fetch shared order items');
    }
    
    if (!sharedItems || sharedItems.length === 0) {
      throw new Error('Shared order has no items');
    }
    
    console.log('[CartContext] Found', sharedItems.length, 'items in shared order');
    
    // Get user information
    const userFullName = user.user_metadata?.full_name || user.user_metadata?.name || null;
    const userPhone = user.user_metadata?.phone || null;
    const userEmail = user.email || null;
    
    // Create new order for the user
    const { data: newOrder, error: orderError } = await supabase
      .from('orders')
      .insert({
        user_id: user.id,
        company_name: null,
        contact_name: userFullName,
        email: userEmail,
        phone: userPhone,
        company_type: 'others',
        status: 'STARTED',
        step: 'CART_STARTED'
      })
      .select()
      .single();
    
    if (orderError || !newOrder) {
      console.error('[CartContext] Error creating order:', orderError);
      throw new Error('Failed to create order');
    }
    
    console.log('[CartContext] New order created:', newOrder.id);
    
    // Copy order items to new order
    const newOrderItems = sharedItems.map(item => ({
      order_id: newOrder.id,
      job_position_id: item.job_position_id,
      quantity: item.quantity,
      seniority_level: item.seniority_level,
      english_level: item.english_level,
      contract_duration: item.contract_duration,
      amount: item.amount
    }));
    
    const { data: insertedItems, error: itemsError } = await supabase
      .from('order_items')
      .insert(newOrderItems)
      .select();
    
    if (itemsError || !insertedItems) {
      console.error('[CartContext] Error copying order items:', itemsError);
      throw new Error('Failed to copy order items');
    }
    
    console.log('[CartContext] Copied', insertedItems.length, 'items to new order');
    
    // Update cart state
    setOrderId(newOrder.id);
    const cartItems = insertedItems.map(item => ({
      job_position_id: item.job_position_id,
      quantity: item.quantity,
      seniority_level: item.seniority_level,
      english_level: item.english_level,
      contract_duration: item.contract_duration
    }));
    setItems(cartItems);
    setItemCount(cartItems.reduce((sum, item) => sum + item.quantity, 0));
    
    console.log('[CartContext] Cart updated with copied items');
    
    return newOrder.id;
  };

  return (
    <CartContext.Provider value={{
      items,
      itemCount,
      orderId,
      loading,
      addItem,
      removeItem,
      updateItemQuantity,
      updateQuantity,
      clearCart,
      syncToOrder,
      refreshCart,
      copyItemsFromSharedOrder
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
